import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Factory } from "lucide-react";

const PROC_TYPES = ["nettoyage", "découpe", "séchage", "conditionnement", "emballage", "transformation"];

export default function Transformation() {
  const { t, lang } = useI18n();
  const [lots, setLots] = useState([]);
  const [open, setOpen] = useState(false);
  const [newL, setNewL] = useState({ type: "conditionnement", source_product: "", input_qty: "", output_qty: "", unit: "kg", batch: "", production_date: "", expiry_date: "", operator: "" });

  async function load() {
    const r = await api.get("/processing");
    setLots(r.data.sort((a, b) => (b.production_date || "").localeCompare(a.production_date || "")));
  }
  useEffect(() => { load(); }, []);

  async function addLot() {
    if (!newL.source_product || !newL.batch) return toast.error("Produit & lot requis");
    try {
      const input = parseFloat(newL.input_qty || 0);
      const output = parseFloat(newL.output_qty || 0);
      await api.post("/processing", {
        ...newL, input_qty: input, output_qty: output, loss_qty: Math.max(0, input - output),
        production_date: newL.production_date ? new Date(newL.production_date).toISOString() : new Date().toISOString(),
        expiry_date: newL.expiry_date ? new Date(newL.expiry_date).toISOString() : null,
      });
      setOpen(false); setNewL({ type: "conditionnement", source_product: "", input_qty: "", output_qty: "", unit: "kg", batch: "", production_date: "", expiry_date: "", operator: "" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delLot(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/processing/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-6" data-testid="transformation-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Agro-industrie</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("transformation")}</h1>
          <p className="text-muted-foreground mt-1">{lots.length} {t("batches")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-lot-btn"><Plus className="w-4 h-4 mr-2" />{t("transformation")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("batch")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("type")}</Label>
                <Select value={newL.type} onValueChange={(v) => setNewL({ ...newL, type: v })}>
                  <SelectTrigger data-testid="lot-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROC_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("batch")}</Label><Input data-testid="lot-batch" placeholder="LOT-2026-…" value={newL.batch} onChange={(e) => setNewL({ ...newL, batch: e.target.value })} /></div>
              <div className="col-span-2"><Label>{t("product")}</Label><Input data-testid="lot-product" value={newL.source_product} onChange={(e) => setNewL({ ...newL, source_product: e.target.value })} /></div>
              <div><Label>{t("input_qty")}</Label><Input type="number" value={newL.input_qty} onChange={(e) => setNewL({ ...newL, input_qty: e.target.value })} /></div>
              <div><Label>{t("output_qty")}</Label><Input type="number" value={newL.output_qty} onChange={(e) => setNewL({ ...newL, output_qty: e.target.value })} /></div>
              <div><Label>{t("production_date")}</Label><Input type="date" value={newL.production_date} onChange={(e) => setNewL({ ...newL, production_date: e.target.value })} /></div>
              <div><Label>DLC/DDM</Label><Input type="date" value={newL.expiry_date} onChange={(e) => setNewL({ ...newL, expiry_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>{t("operator")}</Label><Input value={newL.operator} onChange={(e) => setNewL({ ...newL, operator: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addLot} data-testid="lot-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border bg-secondary/50">
              <tr>
                <th className="p-3">{t("batch")}</th><th>{t("type")}</th><th>{t("product")}</th>
                <th>{t("input_qty")}</th><th>{t("output_qty")}</th><th>{t("losses")}</th>
                <th>DLC/DDM</th><th>{t("operator")}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l.id} className="border-b border-border/60 last:border-0" data-testid={`lot-row-${l.id}`}>
                  <td className="p-3 font-mono text-xs font-semibold flex items-center gap-2"><Factory className="w-4 h-4 text-primary" />{l.batch}</td>
                  <td><Badge variant="secondary">{l.type}</Badge></td>
                  <td className="font-medium">{l.source_product}</td>
                  <td>{l.input_qty} {l.unit}</td>
                  <td>{l.output_qty} {l.unit}</td>
                  <td className={l.loss_qty > 0 ? "text-destructive" : ""}>{l.loss_qty} {l.unit}</td>
                  <td>{formatDate(l.expiry_date, lang) || "—"}</td>
                  <td>{l.operator || "—"}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => delLot(l.id)}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
              {lots.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-muted-foreground">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
